export interface TrialAccessState {
  onboardingComplete: boolean | null;
  ownsBusiness: boolean;
  isStaff: boolean;
  isSupportCollaborator: boolean;
  subscriptionStatus: string | null;
  stripeCustomerId: string | null;
  nextBillingDate: string | null;
}

export interface ComplimentaryTrialRecord {
  onboardingComplete: boolean;
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
  stripeCustomerId: string | null;
  billingCycleAnchor: string | null;
  nextBillingDate: string | null;
}

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Verify the server-owned fields returned by the onboarding completion update.
 * Stripe-backed accounts are intentionally excluded because their dates belong
 * to Stripe rather than the complimentary-trial trigger.
 */
export function complimentaryTrialIssue(
  record: ComplimentaryTrialRecord,
): string | null {
  if (!record.onboardingComplete) return "onboarding was not marked complete";
  if (record.stripeCustomerId) return null;
  if (record.subscriptionTier !== "premium") return "the Premium plan was not activated";
  if (record.subscriptionStatus !== "trialing") return "the trial is not active";
  if (!record.billingCycleAnchor || !record.nextBillingDate) {
    return "the trial dates are missing";
  }

  const startsAt = Date.parse(record.billingCycleAnchor);
  const endsAt = Date.parse(record.nextBillingDate);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt)) {
    return "the trial dates are invalid";
  }
  if (Math.abs(endsAt - startsAt - FOURTEEN_DAYS_MS) > 1000) {
    return "the trial duration is not 14 days";
  }
  return null;
}

/**
 * Complimentary trials do not have a Stripe customer. An owner's trial is over as
 * soon as the deadline passes, even if the hourly expiry job has not yet changed
 * `subscription_status` to `trial_expired`. No deadline means it never expires.
 */
export function complimentaryTrialHasEnded(
  state: Pick<TrialAccessState, "subscriptionStatus" | "stripeCustomerId" | "nextBillingDate">,
  now = new Date(),
): boolean {
  if (state.subscriptionStatus === "trial_expired") return true;

  if (
    state.subscriptionStatus !== "trialing" ||
    state.stripeCustomerId ||
    !state.nextBillingDate
  ) {
    return false;
  }

  const trialEndsAt = Date.parse(state.nextBillingDate);
  return Number.isFinite(trialEndsAt) && trialEndsAt <= now.getTime();
}

export function shouldBlockForExpiredTrial(
  state: TrialAccessState,
  now = new Date(),
): boolean {
  if (
    !state.ownsBusiness ||
    !state.onboardingComplete ||
    state.isStaff ||
    state.isSupportCollaborator
  ) {
    return false;
  }

  return complimentaryTrialHasEnded(state, now);
}
